import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import omit from 'lodash/omit';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { labelMinLength, userNameMaxLength } from '../../../app.constants';
import { ConfigService } from '../../../config.service';
import { ConfirmDialog, DialogService } from '../../../dialogs';
import { CoreApiService } from '../../../core-api.service';
import { LoggingService } from '../../../logging.service';
import { NotificationService } from '../../../notification.service';
import { ProfileService } from '../../../profile/profile.service';
import { ProjectsService } from '../../../projects/projects.service';
import { SiteRole } from '../../../shared/enums/site-role.enum';
import { Profile } from '../../../shared/models/profile.model';
import { User } from '../../../shared/models/user.model';
import { TranslationBundle, TranslationService } from '../../../translation.service';

@Component({
  host: { 'test-id': 'user-information' },
  selector: 'user-information',
  templateUrl: './user-information.component.html',
  styleUrls: ['./user-information.component.scss'],
})
export class UserInformationComponent implements OnChanges {
  @Input() user: User
  @Output() afterSave = new EventEmitter<void>()

  form = new FormGroup({
    firstname: new FormControl('', [Validators.required, Validators.minLength(labelMinLength), Validators.maxLength(userNameMaxLength)]),
    lastname: new FormControl('', [Validators.required, Validators.minLength(labelMinLength), Validators.maxLength(userNameMaxLength)]),
    email: new FormControl('', [Validators.required, Validators.email]),
    role: new FormControl(''),
    disabled: new FormControl(false),
  })
  ldapEnabled = false
  roleOptions: { label: string, value: string, disabled: boolean }[] = []

  private messages = TranslationBundle.EMPTY
  private profile: Profile
  private subscriptions = {
    messages: Subscription.EMPTY,
  }

  constructor(
    private config: ConfigService,
    private dialog: DialogService,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notification: NotificationService,
    private profileService: ProfileService,
    private projectsService: ProjectsService,
    private translate: TranslationService,
  ) {
    this.subscriptions.messages = combineLatest([
      this.translate.getBundle('manageUsers'),
      this.config.getSiteSettings(),
      this.profileService.getProfile(),
    ]).subscribe(([manageUsers, settings, profile]) => {
      this.messages = manageUsers;
      this.ldapEnabled = settings.ldap_sync?.group_dn?.length > 0;
      this.profile = profile;
      this.roleOptions = [
        { label: manageUsers.get('userInfo.roles.user'), value: SiteRole.User, disabled: false },
        { label: manageUsers.get('userInfo.roles.developer'), value: SiteRole.Developer, disabled: false },
        { label: manageUsers.get('userInfo.roles.site_admin'), value: SiteRole.SiteAdmin, disabled: this.ldapEnabled },
      ];
      this.resetFormGroup();
    });
    this.form.get('disabled').valueChanges.subscribe(() => {
      this.disableToggled();
    });

    // editing of user details within a core instance is not allowed in central
    // user info is managed by the central manager service and central sync service
    if (this.config.getCentralConfig().enabled) {
      this.form.controls.firstname.disable()
      this.form.controls.lastname.disable()
      this.form.controls.email.disable()
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.user) {
      this.resetFormGroup();
    }
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  resetFormGroup(): void {
    this.form.reset({
      firstname: this.user?.firstname || '',
      lastname: this.user?.lastname || '',
      email: this.user?.email || '',
      role: this.user ? SiteRole.getPrimaryRole(this.user) : SiteRole.User,
      disabled: this.user?.disabled ?? false,
    }, { emitEvent: false });
    const disabledControl = this.form.get('disabled');
    // don't allow users to disable themselves
    if (this.profile?._id !== this.user?._id) {
      disabledControl.enable();
    } else {
      disabledControl.disable();
    }
  }

  getErrorText(controlName: string): string {
    const { errors } = this.form.get(controlName);

    let translatedControlName: string;
    switch (controlName) {
      case 'firstname':
        translatedControlName = this.messages.get('userInfo.form.firstName');
        break;
      case 'lastname':
        translatedControlName = this.messages.get('userInfo.form.lastName');
        break;
    }

    // we should be limited to one error at a time
    const error = Object.keys(errors)[0];
    const message = this.messages.get(`userInfo.validation.${error}`);
    let interpolated: string;
    switch (error) {
      case 'minlength':
        interpolated = this.translate.interpolate(message, 2);
        break;
      case 'maxlength':
        interpolated = this.translate.interpolate(message, 32);
        break;
      case 'required':
        interpolated = message;
        break;
    }
    return `${translatedControlName} ${interpolated}`;
  }

  disableToggled(): void {
    const disabledSlider = this.form.get('disabled');
    if (disabledSlider.dirty) {
      const clearPermissions$: Observable<boolean> = disabledSlider.value ? this.dialog.open(ConfirmDialog, {
        // user is changing to disabled, warn them!
        data: {
          content: this.messages.get('userInfo.form.loginDisableWarning'),
        },
        width: '600px',
      }).afterClosed() as Observable<boolean> : of(false);
      clearPermissions$.pipe(
        switchMap(clearPermissions => {
          const payload = { disabled: disabledSlider.value };
          const params = disabledSlider.value && clearPermissions ? { clear_permissions: true } : {};
          return this.coreApi.users.put(this.user._id, payload, params);
        }),
      ).subscribe({
        next: () => {
          this.notification.success(`userManagement.${disabledSlider.value ? 'disableUser' : 'enableUser'}.success`);
          this.projectsService.getUsers({ refresh: true });
          this.afterSave.emit();
        },
        error: error => {
          this.logger.error(error);
          this.notification.error(`userManagement.${disabledSlider.value ? 'disableUser' : 'enableUser'}.error`);
        },
      });
    }
  }

  save(): void {
    const payload: Partial<User> = omit(this.form.value, 'role');
    payload.roles = [this.form.value.role as SiteRole || SiteRole.User];
    this.coreApi.users.put(this.user._id, payload).subscribe({
      next: () => {
        this.notification.success('userManagement.updateInfo.success');
        this.projectsService.getUsers({ refresh: true });
        this.afterSave.emit();
      },
      error: error => {
        this.logger.error(error);
        this.notification.error('userManagement.updateInfo.error');
      },
    });
  }
}
